import React from 'react';
import { ReactSVG } from 'react-svg';
import type { OverridableComponent } from '@mui/material/OverridableComponent';
import type { SvgIconTypeMap } from '@mui/material/SvgIcon';

import { links } from 'data/constants';
import { access, hasAccess, resources } from 'utils/permissions';

// Types for sidebar navigation structure
export interface SidebarSubItem {
  name: string;
  path: string;
  permission: boolean;
}

export interface SidebarItem {
  name: string;
  path?: string;
  permission: boolean;
  subItems?: SidebarSubItem[];
}

export interface SidebarSection {
  name: string;
  Icon: OverridableComponent<SvgIconTypeMap<Record<string, unknown>, 'svg'>>;
  permission: boolean;
  items: SidebarItem[];
}

export interface SidebarItemsMap {
  [key: string]: SidebarSection;
}

export const sidebarItemsMap: SidebarItemsMap = {
  dashboard: {
    name: 'All Tenants',
    Icon: () => (
      <ReactSVG src={`${links.imagesBaseUrl}/icons/icon_customers.svg`} />
    ),
    permission: hasAccess([resources.CUSTOMERS, access.READ]),
    items: [
      {
        name: 'All Tenants',
        path: '/customers/all-tenants',
        permission: hasAccess([resources.CUSTOMERS, access.READ])
      },
      {
        name: 'Networks',
        path: '/customers/networks',
        permission: hasAccess([resources.RD_NETWORKS, access.READ])
      },
      {
        name: 'Applications',
        path: '/customers/applications',
        permission: hasAccess([resources.RD_APPLICATIONS, access.READ])
      },
      {
        name: 'Cancellations',
        path: '/customers/cancellations',
        permission: hasAccess(
          [resources.CANCELLATIONS_REQUEST, access.READ],
          [resources.TENANT_SENSITIVE_DATA, access.READ]
        )
      }
    ]
  },
  interactions: {
    name: 'Interactions',
    Icon: () => (
      <ReactSVG src={`${links.imagesBaseUrl}/icons/icon_userSettings.svg`} />
    ),
    permission: hasAccess([resources.QBR_BOARD, access.READ]),
    items: [
      {
        name: 'Satisfaction Board',
        path: '/interactions/satisfaction-board',
        permission: hasAccess(
          [resources.SATISFACTION_BOARD, access.READ],
          [resources.TENANT_SENSITIVE_DATA, access.READ]
        )
      },
      {
        name: 'QBRs Board',
        path: '/interactions/qbrs-board',
        permission: hasAccess([resources.QBR_BOARD, access.READ])
      },
      {
        name: 'Maintenance Windows',
        path: '/interactions/maintenance-windows',
        permission: hasAccess([resources.RD_MAINTENANCE, access.READ])
      },
      {
        name: 'Sentiment Board',
        path: '/interactions/sentiment-board',
        permission: hasAccess(
          [resources.SENTIMENT_BOARD, access.READ],
          [resources.TENANT_SENSITIVE_DATA, access.READ]
        )
      }
    ]
  },
  cs: {
    name: 'CS',
    Icon: () => <ReactSVG src={`${links.imagesBaseUrl}/icons/icon_cs.svg`} />,
    permission:
      hasAccess([resources.CS_NETWORK_ROLLOUT, access.READ]) ||
      hasAccess([resources.NETWORK_EDGE, access.READ]),
    items: [
      {
        name: 'Agent Rollout',
        permission: hasAccess([resources.CS_AGENT_ROLLOUT, access.READ]),
        subItems: [
          {
            name: 'Agent Rollout',
            path: '/cs/agent-rollout',
            permission: hasAccess([resources.CS_AGENT_ROLLOUT, access.READ])
          },
          {
            name: 'Agent Rollout Logs',
            path: '/cs/versions-activity-log',
            permission: hasAccess([resources.CUSTOMERS_AGENT_LOGS, access.READ])
          }
        ]
      },
      {
        name: 'Network Rollout',
        path: '/cs/network-rollout',
        permission: hasAccess([resources.CS_NETWORK_ROLLOUT, access.READ])
      },
      {
        name: 'Cloud Management Logs',
        path: '/cs/cloud-management-log',
        permission: hasAccess([resources.CLOUD_MANAGEMENT, access.READ])
      },
      {
        name: 'Edge Management',
        path: '/cs/edges',
        permission: hasAccess([resources.NETWORK_EDGE, access.READ])
      },
      {
        name: 'Activity Logs',
        path: '/cs/activity-log',
        permission: hasAccess([resources.SETTINGS_ACTIVITY_LOG, access.READ])
      }
    ]
  },
  product: {
    name: 'Product',
    Icon: () => (
      <ReactSVG src={`${links.imagesBaseUrl}/icons/icon_product.svg`} />
    ),
    permission: hasAccess([resources.CUSTOMERS, access.READ]),
    items: [
      {
        name: 'Feature Adoption',
        path: '/product/features-adoption',
        permission: hasAccess([resources.CUSTOMERS, access.READ])
      },
      {
        name: 'Feature Flags',
        permission: hasAccess([resources.PRODUCT_FEATURE_FLAG, access.READ]),
        subItems: [
          {
            name: 'Feature Flag Rollout',
            path: '/product/feature-flag',
            permission: hasAccess([resources.PRODUCT_FEATURE_FLAG, access.READ])
          },
          {
            name: 'Feature Flag Logs',
            path: '/product/ff-activity-log',
            permission: hasAccess([
              resources.PRODUCT_FEATURE_FLAG_LOGS,
              access.READ
            ])
          }
        ]
      }
    ]
  },
  arr: {
    name: 'ARR',
    Icon: () => <ReactSVG src={`${links.imagesBaseUrl}/icons/icon_arr.svg`} />,
    permission: hasAccess([resources.CUSTOMERS, access.READ]),
    items: [
      {
        name: 'ARR Activity',
        path: '/arr/arr-activity',
        permission: hasAccess(
          [resources.MRR_ACTIVITY, access.READ],
          [resources.TENANT_SENSITIVE_DATA, access.READ]
        )
      },
      {
        name: 'ARR Cohort',
        path: '/arr/arr-cohort',
        permission: hasAccess([resources.CUSTOMERS, access.READ])
      },
      {
        name: 'Churn Cohort',
        path: '/arr/churn-cohort',
        permission: hasAccess([resources.CUSTOMERS, access.READ])
      }
    ]
  },
  marketing: {
    name: 'Marketing',
    Icon: () => (
      <ReactSVG src={`${links.imagesBaseUrl}/icons/icon_marketing.svg`} />
    ),
    permission: hasAccess([resources.CUSTOMERS, access.READ]),
    items: [
      {
        name: 'Collection Cohort',
        path: '/marketing/collection-cohort',
        permission: hasAccess([resources.CUSTOMERS, access.READ])
      }
    ]
  },
  copilot: {
    name: 'AI Tools',
    Icon: () => <ReactSVG src={`${links.imagesBaseUrl}/icons/icon_cs.svg`} />,
    permission: hasAccess([resources.COPILOT_CHAT, access.READ]),
    items: [
      {
        name: 'Copilot Chat',
        path: '/copilot-chat',
        permission: hasAccess([resources.COPILOT_CHAT, access.READ])
      }
    ]
  }
};
